import 'antd/dist/reset.css';
import 'react-toastify/dist/ReactToastify.css';

import { ConfigProvider } from 'antd';

import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { BuildInfo, VersionInfo } from './Config';
import { msalInstance } from './shared/common/auth/authConfig';
import { handleError } from './shared/common/HandleErrors';
import { isBedBoardIntakeForm, isBedBoardIntakeFormOnly } from './shared/common/helpers';
import { bedBoardIntakeFormTheme, globalTheme } from './shared/common/theme';
import { store } from './shared/store';

let appTheme = globalTheme;

if (BuildInfo.IsBedBoard) {
    if (isBedBoardIntakeFormOnly()) {
        appTheme = bedBoardIntakeFormTheme;
    }
}

if (window.location?.search?.includes('version')) {
    console.log('VersionInfo:');
    console.log(VersionInfo);
}

if (window.location?.search?.includes('build')) {
    console.log('BuildInfo:');
    console.log(BuildInfo);
}

if (window.location?.search?.includes('env')) {
    console.log('EnvironmentInfo:');
    console.log(process.env);
}

function importBuildTarget() {
    if (BuildInfo.IsBedBoard) {
        return isBedBoardIntakeForm() ? import('./bedboard/App-NoAuth') : import('./bedboard/App');
    }

    return import('./post-acute/App');
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

importBuildTarget().then(({ default: TargetApp }) => {
    // For noauth area, don't apply the msal component
    if (TargetApp.displayName.includes('AppNoAuth')) {
        root.render(
            <Provider store={store}>
                <ConfigProvider theme={globalTheme} tag={{ variant: 'solid' }} getPopupContainer={() => document.getElementById('popup-containers')}>
                    <TargetApp />
                </ConfigProvider>
            </Provider>
        );
    } else {
        msalInstance
            .initialize()
            .then(async () => {
                // Default to using the first account if no account is active on page load
                if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
                    // Account selection logic is app dependent. Adjust as needed for different use cases.
                    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
                }

                // Optional - Enable account storage events to sync auth state across tabs/windows
                // This method may not be available in all MSAL versions
                if (typeof (msalInstance as any).enableAccountStorageEvents === 'function') {
                    (msalInstance as any).enableAccountStorageEvents();
                }

                // Register event callback with cleanup management
                msalInstance.addEventCallback(async (event: EventMessage) => {
                    if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                        const authResult = event.payload as AuthenticationResult | undefined;
                        const account = authResult?.account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

                        if (account) {
                            if (!msalInstance.getActiveAccount()) {
                                console.log('MSAL auth success, setting active account');
                            }

                            msalInstance.setActiveAccount(account);
                        } else {
                            console.warn('MSAL auth success event without an account payload/cache.');
                        }
                    } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                        // Token acquisition failures are handled inside acquireAccessToken()
                        // with retries and single-flight deduplication. Only log here to
                        // avoid triggering additional concurrent renewal attempts.
                        console.warn('MSAL token acquisition failed:', (event.error as any)?.errorCode || event.error);
                    }
                });

                root.render(
                    <Provider store={store}>
                        <MsalProvider instance={msalInstance}>
                            <ConfigProvider theme={appTheme} tag={{ variant: 'solid' }} getPopupContainer={() => document.getElementById('popup-containers')}>
                                <TargetApp />
                            </ConfigProvider>
                        </MsalProvider>
                    </Provider>
                );
            })
            .catch((err) => {
                handleError(err);
            });
    }
});

const orgConsoleError = console.error;

console.error = (...args) => {
    const textValue = String(args[0]);

    if (textValue.includes(`Can't perform a React state update on a component that hasn't mounted yet`)) return;
    if (textValue.includes(`Support for defaultProps will be removed from function components in a future major release`)) return;
    if (textValue.includes(`findDOMNode is deprecated and will be removed in the next major release.`)) return;
    if (textValue.includes('Invalid DOM property')) return;
    if (textValue.includes('Consider using an empty string to clear the component or')) return;
    if (textValue.includes('Support for defaultProps will be removed from memo components in a future major release')) return;
    if (
        textValue.includes('InteractionRequiredAuthError') ||
        textValue.includes('interaction_required') ||
        textValue.includes('refresh_token_expired') ||
        textValue.includes('block_iframe_reload')
    ) {
        return;
    }

    orgConsoleError(...args);
};
